import React, { InputHTMLAttributes, useState } from 'react';
import Tooltip from '../Tooltip';
import baseStyles from '../form-field-base.module.css';
import styles from './FormInput.module.css';

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  required?: boolean;
  warning?: boolean;
  warningTitle?: string;
  containerClassName?: string;
  loading?: boolean;
  endAdornment?: React.ReactNode;
}

const FormInput: React.FC<FormInputProps> = ({
  label,
  error,
  required: isRequired,
  warning,
  warningTitle,
  id,
  containerClassName = '',
  className = '',
  readOnly,
  type,
  loading = false,
  endAdornment,
  ...rest
}) => {
  const isPassword = type === 'password';
  const [showPassword, setShowPassword] = useState(false);
  const hasEndAdornment = Boolean(endAdornment) && !isPassword;

  const inputCls = [
    styles.input,
    readOnly ? styles.inputReadOnly : '',
    error ? styles.inputError : '',
    isPassword ? styles.inputPassword : '',
    hasEndAdornment ? styles.inputWithEndAdornment : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={`${baseStyles.fieldGroup} ${containerClassName}`}>
      {label && (
        <label htmlFor={id} className={baseStyles.label}>
          {label}
          {isRequired && <span className={baseStyles.required}>*</span>}
          {warning && (
            <Tooltip content={warningTitle}>
              <span className={styles.warningIcon} aria-label={warningTitle || 'Aviso'}>
                warning
              </span>
            </Tooltip>
          )}
        </label>
      )}
      <div className={styles.inputWrapper}>
        <input
          id={id}
          className={inputCls}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          autoComplete="off"
          type={isPassword && showPassword ? 'text' : type}
          {...rest}
        />
        {loading && (
          <span className={styles.inputSpinner} role="status" aria-label="Carregando" />
        )}
        {isPassword && (
          <button
            type="button"
            className={styles.passwordToggle}
            onClick={() => setShowPassword((prev) => !prev)}
            tabIndex={-1}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              {showPassword ? 'visibility_off' : 'visibility'}
            </span>
          </button>
        )}
        {hasEndAdornment && <div className={styles.endAdornment}>{endAdornment}</div>}
      </div>
      {error && <span className={baseStyles.errorText}>{error}</span>}
    </div>
  );
};

export default FormInput;
